import { CalendarCheck, MessageCircle, Phone, HeartHandshake, Stethoscope, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { PHONE_TEL } from "./data";

const HERO_IMG = "https://images.unsplash.com/photo-1728342057953-94bfad8f0e7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBkZW50YWwlMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODgwMjE3MTd8MA&ixlib=rb-4.1.0&q=85";

const trust = [
    { icon: HeartHandshake, label: "Patient-Focused Care" },
    { icon: Stethoscope, label: "Comprehensive Dental Services" },
    { icon: ClipboardList, label: "Personalized Consultation" },
];

export const Hero = () => {
    return (
        <section id="home" className="relative overflow-hidden bg-white pt-[72px]">
            <div className="pointer-events-none absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-teal-50 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute top-64 -left-40 h-[320px] w-[320px] rounded-full bg-sky-50 blur-3xl" aria-hidden="true" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div data-testid="hero-content">
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-teal-600 mb-5">
                        Dental Care in Liluah, Howrah
                    </p>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.08]">
                        Healthy Smiles Start With{" "}
                        <span className="relative inline-block text-teal-600">
                            Better Dental Care.
                            <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 220 10" fill="none" aria-hidden="true">
                                <path d="M3 7C60 2.5 160 2.5 217 7" stroke="#14b8a6" strokeWidth="4" strokeLinecap="round" opacity="0.4" />
                            </svg>
                        </span>
                    </h1>
                    <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
                        Professional and patient-focused dental care for healthier teeth, confident smiles and better oral health in Liluah, Howrah.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <a
                            href="#appointment"
                            data-testid="hero-book-appointment-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-teal-600 px-7 py-3.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(13,148,136,0.4)] transition-all duration-200 hover:bg-teal-700 hover:-translate-y-0.5"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Book Appointment
                        </a>
                        <button
                            type="button"
                            data-testid="hero-whatsapp-btn"
                            onClick={() => toast.info("WhatsApp booking will be connected in the next update.")}
                            className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 transition-all duration-200 hover:border-teal-300 hover:text-teal-700 hover:-translate-y-0.5"
                        >
                            <MessageCircle className="h-4 w-4 text-teal-600" />
                            WhatsApp Us
                        </button>
                        <a
                            href={PHONE_TEL}
                            data-testid="hero-call-clinic-btn"
                            className="inline-flex items-center gap-2 rounded-full px-4 py-3.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-teal-700"
                        >
                            <Phone className="h-4 w-4 text-teal-600" />
                            Call Clinic
                        </a>
                    </div>

                    <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-4" data-testid="hero-trust-indicators">
                        {trust.map((t) => (
                            <li key={t.label} className="flex items-center gap-2.5">
                                <span className="h-9 w-9 rounded-full bg-teal-50 flex items-center justify-center">
                                    <t.icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-teal-600" strokeWidth={1.8} />
                                </span>
                                <span className="text-sm font-semibold text-slate-700">{t.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative" data-testid="hero-image-wrapper">
                    <div className="absolute -inset-4 rounded-[2rem] bg-teal-50 rotate-2" aria-hidden="true" />
                    <img
                        src={HERO_IMG}
                        alt="Modern dental clinic interior at The Tooth Care Dental Clinic"
                        className="relative rounded-3xl object-cover w-full h-[340px] sm:h-[420px] lg:h-[520px] shadow-[0_24px_60px_rgba(2,44,40,0.18)]"
                        loading="eager"
                    />
                    <div className="absolute -bottom-6 left-6 sm:left-8 rounded-2xl bg-white px-5 py-4 shadow-[0_16px_40px_rgba(0,0,0,0.12)] flex items-center gap-3">
                        <span className="h-10 w-10 rounded-full bg-teal-600 flex items-center justify-center">
                            <HeartHandshake className="h-5 w-5 text-white" strokeWidth={1.8} />
                        </span>
                        <span>
                            <span className="block text-sm font-bold text-slate-900">Gentle, Caring Dentistry</span>
                            <span className="block text-xs text-slate-500">Comfort-first consultations</span>
                        </span>
                    </div>
                </div>
            </div>
        </section>
    );
};
